import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Summary from './sections/summary'
import FieldSizeSection from './sections/field-size-section'
import DateTimeSection from './sections/date-time-section'
import PersonInfoSection from './sections/person-info-section'
import PaymentSection from './sections/payment-section'
import MobileButtons from './sections/mobile-buttons'
import Complete from './sections/complete'
import { caclulateFee } from '../../shop/common'
import { createGlobalStyle } from 'styled-components'

const GlobalStyles = createGlobalStyle`
  #booking { --booking-plugin-color: ${props => props.color}; }
`

function BookingPlugin(props) {
  const { step, person, price } = props
  const [verifyForm, setVerifyForm] = useState(null)
  const [confirmForm, setConfirmForm] = useState(null)
  const paymentStep = step == 'payment'
  const completed = step == 'complete'

  const goToNextStep = () => {
    if (step == 'time' && _.isEmpty(props.selectedInterval)) {
      return props.setAlert({ text: 'Please specify time', type: 'alert' })
    }
    props.setStep(nextStep())
  }

  const goToPrevStep = () => {
    if (step == 'person' && person.id) return props.setPerson(_.omit(person, ['id']))

    props.setStep(prevStep())
  }

  const nextStep = () => {
    switch (step)
    {
      case 'field':
        return 'time'
      case 'time':
        return 'person'
      default: 
        return 'time'
    }
  }

  const prevStep = () => {
    switch (step)
    {
      case 'time':
        return 'field'
      case 'person':
        return 'time'
      default: 
        return 'field'
    }
  }

  const paymentAmounts = () => {
    if (!paymentStep && !completed) return {}

    const {fee, feeInPrice} = caclulateFee(props.fees, price)
    const totalAmount = price + fee
    const totalFee = fee + feeInPrice

    return { totalAmount, totalFee }
  }

  const { totalAmount, totalFee } = paymentAmounts()
  return <div className='row booking-content'>
    { !completed && 
      [ <GlobalStyles color={props.mainColor} key='styles' />,
        <div className='col-xs-12 col-md-4' key='summary'>
          <Summary
            step={step}
            price={price}
            currency={props.paymentParams.currency}
            fieldSizes={props.fieldSizes}
            selectedSize={props.selectedSize}
            selectedDate={props.selectedDate}
            selectedDuration={props.selectedDuration}
            selectedInterval={props.selectedInterval}
            totalAmount={totalAmount}
            totalFee={totalFee}
          />
        </div>,
        <div className='col-xs-12 col-md-8 booking-content__main' key='content'>
          <FieldSizeSection 
            fieldSizes={props.fieldSizes}
            selectedSize={props.selectedSize}
            currency={props.paymentParams.currency}
            step={step}
            onSelectSize={props.selectSize}
            onNextStep={goToNextStep}
          />
          <DateTimeSection 
            step={step}
            alert={props.alert}
            isLoading={props.isLoading}
            selectedSize={props.selectedSize}
            selectedDate={props.selectedDate}
            selectedDuration={props.selectedDuration}
            selectedInterval={props.selectedInterval}
            intervals={props.intervals}
            setAlert={props.setAlert}
            onSelectDate={props.selectDate}
            onSelectDuration={props.selectDuration}
            fetchIntervals={props.fetchIntervals}
            fetchPrice={props.fetchPrice}
            selectInterval={props.selectInterval}
            onNextStep={goToNextStep}
            onPrevStep={goToPrevStep}
          />
          <PersonInfoSection
            step={step}
            person={person}
            alert={props.alert}
            verify={func => setVerifyForm(() => func())}
            confirm={func => setConfirmForm(() => func())}
            verifyPerson={props.verifyPerson}
            isLoading={props.isLoading}
            setAlert={props.setAlert}
            confirmCode={props.confirmCode}
            resendCode={props.resendCode}
            onNextStep={goToNextStep}
            onPrevStep={goToPrevStep}
          />
          <PaymentSection 
            token={props.token}
            step={step}
            alert={props.alert}
            location={props.location}
            price={price}
            totalAmount={totalAmount}
            totalFee={totalFee}
            isLoading={props.isLoading}
            paymentParams={props.paymentParams}
            fieldSizes={props.fieldSizes}
            selectedSize={props.selectedSize}
            selectedDate={props.selectedDate}
            selectedDuration={props.selectedDuration}
            selectedInterval={props.selectedInterval}
            onPayment={props.bookField}
          />
          { !paymentStep && 
            <Summary
              mobile
              step={step}
              price={price}
              currency={props.paymentParams.currency}
              fieldSizes={props.fieldSizes}
              selectedSize={props.selectedSize}
              selectedDate={props.selectedDate}
              selectedDuration={props.selectedDuration}
              selectedInterval={props.selectedInterval}
              totalAmount={totalAmount}
              totalFee={totalFee}
            />
          }
          <MobileButtons
            step={step}
            person={person}
            verifyForm={verifyForm}
            confirmForm={confirmForm}
            goToNextStep={goToNextStep}
            goToPrevStep={goToPrevStep}
          />
        </div>
      ]
    }
    { completed && 
      <Complete
        fieldSizes={props.fieldSizes}
        selectedSize={props.selectedSize}
        selectedDate={props.selectedDate}
        selectedDuration={props.selectedDuration}
        selectedInterval={props.selectedInterval}
        totalAmount={totalAmount}
        currency={props.paymentParams.currency}
        onBookMore={props.bookMore}
      />
    }
    <img className='plei-logo desktop-only' src='/images/powered-by-plei.svg'/>
  </div>
}

BookingPlugin.propTypes = {
  token: PropTypes.string.isRequired,
  mainColor: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  fees: PropTypes.object.isRequired,
  paymentParams: PropTypes.object.isRequired,
  fieldSizes: PropTypes.object.isRequired,
  selectedSize: PropTypes.string.isRequired,
  selectedDate: PropTypes.object.isRequired,
  selectedDuration: PropTypes.string.isRequired,
  intervals: PropTypes.array.isRequired,
  selectedInterval: PropTypes.object.isRequired,
  price: PropTypes.number,
  step: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  alert: PropTypes.object,
  person: PropTypes.object,
  selectSize: PropTypes.func.isRequired,
  selectDate: PropTypes.func.isRequired,
  selectDuration: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  fetchIntervals: PropTypes.func.isRequired,
  fetchPrice: PropTypes.func.isRequired,
  verifyPerson: PropTypes.func.isRequired,
  setPerson: PropTypes.func.isRequired,
  confirmCode: PropTypes.func.isRequired,
  resendCode: PropTypes.func.isRequired,
  bookField: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  selectInterval: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  bookMore: PropTypes.func.isRequired,
}

export default BookingPlugin
